import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import { tag_colorTagNewBackground } from 'ext-ui/style/colors'
import AiStarsIconV2 from 'ext-ui/svg/icon/AiStarsIconV2'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectReceiptsAutoMatchEnabledFromState } from 'src/app/UploadReceiptsAutoMatchPopup/redux/uploadReceiptsAutoMatch.selectors'
import SvgNotificationsInactive from 'src/components/Svgs/SvgNotificationsInactive'
import SvgReceiptUpload from 'src/components/Svgs/SvgReceiptUpload'
import TooltipBody, {
  TooltipBodyDesigns,
} from 'src/components/TooltipBody/TooltipBody'
import { useTranslate } from 'src/localization/Trns'
import { OrganizationsListModel } from 'src/models/organizations.models'
import { UserFeature } from 'src/models/user.models'
import Lnk from 'src/navigation/Lnk'
import { expenseRequirementsReceiptRulesUrl } from 'src/navigation/urls'
import { useQueryUserFeatures } from 'src/queries/auth.queries'
import { orgReceiptRulesQueries } from 'src/queries/organizations/receiptRules/receiptRules.queries'
import {
  createSetIsSendReceiptReminderPopupOpen,
  createSetIsUploadReceiptsAutoMatchPopupOpen,
} from 'src/redux/actions/app.actions'
import { css } from 'styled-components'

interface ReceiptToolsDropdownUtilityToolbarProps {
  organization: OrganizationsListModel
  hasReceiptRulePermissions: boolean
}

const ReceiptToolsDropdownUtilityToolbar: React.FC<
  ReceiptToolsDropdownUtilityToolbarProps
> = (props): React.ReactElement => {
  const translate = useTranslate()

  const receiptsAutoMatchEnabled = useSelector(
    selectReceiptsAutoMatchEnabledFromState,
  )

  const dispatch = useDispatch()
  const {
    SEND_ORGANIZATION_RECEIPT_REMINDER: canSendOrganizationReceiptReminder,
  } =
    useQueryUserFeatures([UserFeature.SEND_ORGANIZATION_RECEIPT_REMINDER])
      .data || {}

  const [
    disableReceiptReminderTooltipOpen,
    setDisableReceiptReminderTooltipOpen,
  ] = useState<boolean>(false)

  const orgReceiptRulesQuery = useQuery(
    orgReceiptRulesQueries.detail(props.organization.id),
  )
  const isReceiptRulesOn = !!orgReceiptRulesQuery.data?.receiptRule?.enabled

  const handleReceiptReminderClick = (e: React.SyntheticEvent): void => {
    e.stopPropagation()
    if (isReceiptRulesOn) {
      dispatch(createSetIsSendReceiptReminderPopupOpen(true))
    }
  }

  const handleReceiptAutoMatchClick = (e: React.SyntheticEvent): void => {
    e.stopPropagation()
    if (receiptsAutoMatchEnabled) {
      dispatch(createSetIsUploadReceiptsAutoMatchPopupOpen(true))
    }
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
        z-index: 5;
        align-items: flex-end;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          width: 234px;
          border-radius: 10px;
          box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.1);
          background-color: ${styleConstants.colors.white};
          padding: 24px 18px;
        `}
      >
        {receiptsAutoMatchEnabled && (
          <div
            data-testid={'receiptsAutoMatchBtn'}
            onClick={(e): void => handleReceiptAutoMatchClick(e)}
            onKeyPress={(e): void => handleReceiptAutoMatchClick(e)}
            role="button"
            tabIndex={0}
            css={css`
              display: flex;
              flex-direction: column;
              margin-bottom: 8px;
              cursor: pointer;
            `}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                ${getFontCss(
                  14,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <span
                aria-hidden="true"
                css={css`
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  width: 20px;
                  height: 20px;
                  margin-right: 4px;
                  border-radius: 4px;
                  background-color: ${tag_colorTagNewBackground};
                `}
              >
                <AiStarsIconV2
                  width={14}
                  height={14}
                  css={css`
                    transform: scaleX(-1);
                  `}
                />
              </span>
              {translate('Auto-Match Receipts')}
            </span>
            <span
              css={css`
                display: flex;
                white-space: normal;
                margin-left: 24px;
                ${getFontCss(
                  10,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate('Upload receipts to automatically match transactions')}
            </span>
          </div>
        )}
        {canSendOrganizationReceiptReminder && (
          <div
            data-testid={'bulkReceiptReminderBtn'}
            onClick={(e): void => handleReceiptReminderClick(e)}
            onKeyPress={(e): void => handleReceiptReminderClick(e)}
            onMouseEnter={(): void => {
              !isReceiptRulesOn && setDisableReceiptReminderTooltipOpen(true)
            }}
            onMouseLeave={(): void => {
              !isReceiptRulesOn && setDisableReceiptReminderTooltipOpen(false)
            }}
            role="button"
            tabIndex={0}
            css={css`
              display: flex;
              flex-direction: column;
              margin-bottom: 8px;
              opacity: ${isReceiptRulesOn ? '1' : '0.5'};
              cursor: pointer;
            `}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                ${getFontCss(
                  14,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <SvgNotificationsInactive
                css={css`
                  transform: scale(calc(18 / 24));
                `}
              />
              {translate('Receipt Reminder')}
            </span>
            <span
              css={css`
                display: flex;
                white-space: normal;
                margin-left: 24px;
                ${getFontCss(
                  10,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate(
                'Send a bulk reminder to all recipients that are missing receipts',
              )}
            </span>
          </div>
        )}
        {props.hasReceiptRulePermissions && (
          <Lnk
            href={expenseRequirementsReceiptRulesUrl(props.organization.id)}
            ariaLabel={'businessRules'}
            css={css`
              display: flex;
              flex-direction: column;
            `}
            testId={'receiptRulesBtn'}
          >
            <span
              css={css`
                display: flex;
                align-items: center;
                ${getFontCss(
                  14,
                  themeConstants.colors.cta,
                  styleConstants.fontFamilies.medium,
                )};
              `}
            >
              <SvgReceiptUpload
                css={css`
                  transform: scale(calc(18 / 24));
                `}
              />
              {translate('Receipt Rules')}
            </span>
            <span
              css={css`
                display: flex;
                white-space: normal;
                margin-left: 24px;
                ${getFontCss(
                  10,
                  styleConstants.colors.gray_dark,
                  styleConstants.fontFamilies.regular,
                )};
              `}
            >
              {translate('View the receipt rules for your company')}
            </span>
          </Lnk>
        )}
        {disableReceiptReminderTooltipOpen && (
          <div
            css={css`
              display: flex;
              position: absolute;
              right: 240px;
              top: 60px;
              width: 200px;
            `}
          >
            <TooltipBody design={TooltipBodyDesigns.TooltipHover} hideXsmDown>
              {translate(
                "To send a receipt reminder, you'll need to turn on receipt rules",
              )}
            </TooltipBody>
          </div>
        )}
      </div>
    </div>
  )
}

export default ReceiptToolsDropdownUtilityToolbar
